import { Button, Heading, Surface, Text } from '@elladx/design-system';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { settingsApi } from '../api/client';
import type { AppSettings, SenderAddress, ShippingOptions, NotificationSettings, LabelFormat } from '../types';
import { US_STATES, CANADIAN_PROVINCES } from '../constants/regions';
import { LABEL_SIZE_OPTIONS, normalizeLabelCompatibility, defaultSenderAddress, defaultShippingOptions, defaultNotifications } from '../constants/settingsOptions';
import SettingsTabs, { type SettingsSection } from '../components/settings/SettingsTabs';
import PageHeader from '../components/PageHeader';
import SenderAddressSection from '../components/settings/SenderAddressSection';
import ShippingOptionsSection from '../components/settings/ShippingOptionsSection';
import NotificationsSection from '../components/settings/NotificationsSection';
import Modal from '../components/Modal';

function SettingsPage() {
  const { toasts, removeToast, success, error } = useToast();
  const [senderAddress, setSenderAddress] = useState<SenderAddress>(defaultSenderAddress);
  const [shippingOptions, setShippingOptions] = useState<ShippingOptions>(defaultShippingOptions);
  const [notifications, setNotifications] = useState<NotificationSettings>(defaultNotifications);
  const [settingsVersion, setSettingsVersion] = useState('unsaved');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [senderErrors, setSenderErrors] = useState<Partial<Record<keyof SenderAddress, string>>>({});
  const [insuranceError, setInsuranceError] = useState('');
  const [activeSection, setActiveSection] = useState<SettingsSection>('sender');
  const [remoteSettingsChanged, setRemoteSettingsChanged] = useState(false);
  const [latestSettings, setLatestSettings] = useState<AppSettings | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [pendingSession, setPendingSession] = useState<(() => void) | null>(null);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const editRevisionRef = useRef(0);
  const seededVersionRef = useRef<string | null>(null);
  const focusFieldRef = useRef<string | null>(null);
  const latestReviewRequestRef = useRef(0);
  const leaveRequestRef = useRef(0);
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get(), refetchOnReconnect: false });
  const loading = settingsQuery.isLoading;
  const loadError = settingsQuery.isLoadingError
    ? settingsQuery.error instanceof Error ? settingsQuery.error.message : 'Failed to load settings'
    : '';
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    (dirty || saving) && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));

  const acceptSettings = (settings: AppSettings) => {
    const version = settings.updated_at || 'unsaved';
    seededVersionRef.current = version;
    setSenderAddress(settings.sender_address || defaultSenderAddress);
    setShippingOptions(normalizeLabelCompatibility(settings.shipping_options || defaultShippingOptions));
    setNotifications(settings.notifications || defaultNotifications);
    setSettingsVersion(version);
    dirtyRef.current = false;
    setDirty(false);
    setSaved(false);
    setSaveError('');
    setSenderErrors({});
    setInsuranceError('');
    setRemoteSettingsChanged(false);
  };

  useLayoutEffect(() => {
    const settings = settingsQuery.data;
    if (!settings || seededVersionRef.current === (settings.updated_at || 'unsaved')) return;
    if (dirtyRef.current || savingRef.current) {
      // Keep the original compare-and-swap version as well as the draft.
      setRemoteSettingsChanged(true);
      return;
    }
    acceptSettings(settings);
  }, [settingsQuery.data]);

  useEffect(() => {
    if (settingsQuery.isLoadingError) error('Settings Unavailable', loadError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.isLoadingError]);

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current && !savingRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const beforeSessionEnd = (event: Event) => {
      if (!dirtyRef.current && !savingRef.current) return;
      const action = event as CustomEvent<{ proceed: () => void }>;
      event.preventDefault();
      leaveRequestRef.current += 1;
      setPendingSession(() => action.detail.proceed);
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('easypost:before-session-end', beforeSessionEnd);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('easypost:before-session-end', beforeSessionEnd);
    };
  }, []);

  useEffect(() => {
    const field = focusFieldRef.current;
    if (!field) return;
    focusFieldRef.current = null;
    document.getElementById(field)?.focus();
  }, [activeSection, senderErrors, insuranceError]);

  const markEdited = () => {
    dirtyRef.current = true;
    editRevisionRef.current += 1;
    setDirty(true);
    setSaved(false);
    setSaveError('');
  };

  const handleSave = async (): Promise<boolean> => {
    if (savingRef.current || loading || loadError) return false;
    const errors: Partial<Record<keyof SenderAddress, string>> = {};
    for (const field of ['name', 'street1', 'city', 'state', 'zip'] as const) {
      if (!senderAddress[field].trim()) errors[field] = 'This field is required.';
    }
    if (senderAddress.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderAddress.email)) errors.email = 'Enter a valid email address.';
    const insuranceInvalid = shippingOptions.insurance_enabled &&
      (!Number.isFinite(shippingOptions.default_insurance_amount) || shippingOptions.default_insurance_amount <= 0);
    setSenderErrors(errors);
    setInsuranceError(insuranceInvalid ? 'Enter an amount greater than $0.' : '');
    if (Object.keys(errors).length || insuranceInvalid) {
      const firstField = Object.keys(errors)[0];
      setActiveSection(firstField ? 'sender' : 'shipping');
      focusFieldRef.current = firstField ? `sender-${firstField}` : 'insurance-amount';
      setSaveError('Check the highlighted fields before saving.');
      error('Check your settings', 'Check the highlighted fields before saving.');
      return false;
    }
    savingRef.current = true;
    setSaving(true);
    latestReviewRequestRef.current += 1;
    setReviewing(false);
    setLatestSettings(null);
    setSaveError('');
    const revision = editRevisionRef.current;
    try {
      // A mount refetch may still hold the pre-save version. Cancel its query
      // before writing so a late read cannot replace the successful save.
      await queryClient.cancelQueries({ queryKey: ['settings'], exact: true });
      const result = await settingsApi.update({
        sender_address: senderAddress,
        shipping_options: shippingOptions,
        notifications,
        updated_at: settingsVersion,
      });
      const version = result.updated_at || settingsVersion;
      setSettingsVersion(version);
      seededVersionRef.current = result.updated_at || 'unsaved';
      const moreEdits = editRevisionRef.current !== revision;
      dirtyRef.current = moreEdits;
      setDirty(moreEdits);
      setSaved(!moreEdits);
      setRemoteSettingsChanged(false);
      // Do not reseed over edits made while the request was in flight.
      queryClient.setQueryData<AppSettings>(['settings'], result);
      success('Settings Saved', moreEdits ? 'Saved. Your newer edits still need saving.' : 'Your settings have been updated successfully.');
      return !moreEdits;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      const conflict = typeof err === 'object' && err !== null && 'status' in err && err.status === 409;
      if (conflict) setRemoteSettingsChanged(true);
      setSaveError(conflict ? 'Someone updated these settings. Your edits are preserved. Review the latest saved settings before continuing.' : message);
      error('Save Failed', message);
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleSenderChange = (field: keyof SenderAddress, value: string) => {
    markEdited();
    setSenderErrors(previous => ({ ...previous, [field]: undefined }));
    setSenderAddress(previous => ({ ...previous, [field]: value, ...(field === 'country' && value !== previous.country ? { state: '' } : {}) }));
  };
  const handleShippingChange = <K extends keyof ShippingOptions>(field: K, value: ShippingOptions[K]) => {
    markEdited();
    if (field === 'default_insurance_amount' || field === 'insurance_enabled') setInsuranceError('');
    setShippingOptions(previous => {
      const next = { ...previous, [field]: value };
      if (field === 'label_format') {
        const format = value as LabelFormat;
        const selected = LABEL_SIZE_OPTIONS.find(option => option.value === previous.label_size);
        if (!selected?.formats.includes(format)) {
          const compatible = LABEL_SIZE_OPTIONS.find(option => option.formats.includes(format));
          if (compatible) next.label_size = compatible.value;
        }
      }
      return next;
    });
  };
  const handleNotificationChange = (field: keyof NotificationSettings, value: string | boolean) => {
    markEdited();
    setNotifications(previous => ({ ...previous, [field]: value }));
  };
  const stay = () => {
    leaveRequestRef.current += 1;
    if (blocker.state === 'blocked') blocker.reset();
    setPendingSession(null);
  };
  const proceed = () => {
    if (savingRef.current) return;
    dirtyRef.current = false;
    setDirty(false);
    if (blocker.state === 'blocked') blocker.proceed();
    if (pendingSession) pendingSession();
    setPendingSession(null);
  };
  const saveAndLeave = async () => {
    const requestId = ++leaveRequestRef.current;
    if (await handleSave() && requestId === leaveRequestRef.current) proceed();
  };
  const reviewLatest = async () => {
    if (savingRef.current) return;
    const requestId = ++latestReviewRequestRef.current;
    setReviewing(true);
    try {
      const latest = await settingsApi.get();
      if (requestId === latestReviewRequestRef.current && !savingRef.current) setLatestSettings(latest);
    }
    catch (err) {
      if (requestId === latestReviewRequestRef.current) setSaveError(err instanceof Error ? err.message : 'Could not load the latest settings. Your draft is preserved.');
    }
    finally { if (requestId === latestReviewRequestRef.current) setReviewing(false); }
  };

  return <>
    <ToastContainer toasts={toasts} onClose={removeToast} />
    <div className="settings-page">
      <PageHeader title="Settings" description="Sender address, shipping defaults, and notifications for this workspace" />
      {remoteSettingsChanged && !loading && !loadError && <Surface className="settings-conflict">
        <Heading as="h2" variant="title">Settings changed while you were editing</Heading>
        <Text>Your edits are preserved. Review the latest saved settings to resolve the conflict.</Text>
        <Button type="button" variant="secondary" onClick={reviewLatest} disabled={reviewing || saving} isLoading={reviewing}>Review latest settings</Button>
      </Surface>}
      <div className="settings-grid">
        <div className="settings-vnav"><SettingsTabs active={activeSection} onChange={setActiveSection} /></div>
        <Surface className="settings-card" id="settings-panel" role="tabpanel" aria-labelledby={`settings-tab-${activeSection}`} aria-busy={loading}>
          {loading ? <div className="panel" role="status">
            <Heading as="h2" variant="card">Loading settings</Heading><div className="skeleton" aria-hidden="true" style={{ height: '300px' }} />
          </div> : loadError ? <div className="panel">
            <Heading as="h2" variant="card">Settings could not be loaded</Heading><Text role="alert">{loadError}</Text>
            <Text>Retry to load your saved settings.</Text><Button type="button" onClick={() => settingsQuery.refetch()} variant="primary">Retry</Button>
          </div> : <>
            <div className="settings-content">
              {activeSection === 'sender' && <SenderAddressSection address={senderAddress} regions={senderAddress.country === 'CA' ? CANADIAN_PROVINCES : US_STATES} onChange={handleSenderChange} errors={senderErrors} />}
              {activeSection === 'shipping' && <ShippingOptionsSection options={shippingOptions} onChange={handleShippingChange} insuranceError={insuranceError} />}
              {activeSection === 'notifications' && <NotificationsSection notifications={notifications} onChange={handleNotificationChange} />}
            </div>
            <div className="settings-actions settings-save-bar">
              <Text role="status">{saving ? 'Saving changes…' : dirty ? 'Unsaved changes' : saved ? 'All changes saved' : 'Up to date'}</Text>
              <Button onClick={() => { void handleSave(); }} disabled={saving} variant="primary" type="button" isLoading={saving}>{saving ? 'Saving...' : 'Save changes'}</Button>
            </div>
            {saveError && <Text className="settings-save-error" role="alert">{saveError}</Text>}
          </>}
        </Surface>
      </div>
    </div>
    <Modal isOpen={blocker.state === 'blocked' || Boolean(pendingSession)} onClose={stay} title="Leave without saving?">
      <Text>{saving ? 'Wait for the save to finish. Your newer edits will remain available.' : 'You have unsaved settings. Save them or discard your edits before leaving.'}</Text>
      <div className="modal-footer settings-leave-actions">
        <Button type="button" variant="secondary" onClick={stay}>Stay</Button>
        <Button type="button" variant="danger" disabled={saving} onClick={proceed}>Leave without saving</Button>
        <Button type="button" variant="primary" disabled={saving} onClick={() => { void saveAndLeave(); }}>Save and leave</Button>
      </div>
      {saveError && <Text role="alert">{saveError}</Text>}
    </Modal>
    <Modal isOpen={Boolean(latestSettings)} onClose={() => setLatestSettings(null)} title="Latest saved settings">
      {latestSettings && <>
        <Text>Your draft is still available. Using these settings will discard your unsaved edits.</Text>
        <dl className="settings-latest">
          <dt>Sender</dt><dd>{latestSettings.sender_address.name}<br />{latestSettings.sender_address.street1}<br />{latestSettings.sender_address.city}, {latestSettings.sender_address.state} {latestSettings.sender_address.zip}</dd>
          <dt>Labels</dt><dd>{latestSettings.shipping_options.label_format}, {latestSettings.shipping_options.label_size}</dd>
          <dt>Insurance</dt><dd>{latestSettings.shipping_options.insurance_enabled ? `Enabled: $${latestSettings.shipping_options.default_insurance_amount}` : 'Off'}</dd>
          <dt>Tracking emails</dt><dd>{latestSettings.notifications.send_tracking_emails ? 'On' : 'Off'}</dd>
        </dl>
        <div className="modal-footer">
          <Button type="button" variant="secondary" onClick={() => setLatestSettings(null)}>Keep my draft</Button>
          <Button type="button" variant="primary" disabled={saving} onClick={() => {
            if (savingRef.current) return;
            latestReviewRequestRef.current += 1;
            editRevisionRef.current += 1;
            void queryClient.cancelQueries({ queryKey: ['settings'], exact: true });
            acceptSettings(latestSettings);
            queryClient.setQueryData(['settings'], latestSettings);
            setLatestSettings(null);
          }}>Use latest saved settings</Button>
        </div>
      </>}
    </Modal>
  </>;
}
export default SettingsPage;
